import type { LocationFilters } from '../../search-shell/model/shellState';

// One view event per (tab, filter set) per page load.
//
// Keyed by tab AND filter set: editing the filters IS a new search and must report, while returning
// to a tab is not. Module scope on purpose — the layout that reports remounts every time the agent
// opens a delivery and comes back, so a component-lifetime ref would count navigation as adoption.
// Bounded by how many (building, filter set) pairs one page visits.
const reported = new Set<string>();

// CONSUMES the key — asking is what marks the search reported, so callers must ask last (after the
// query has data) and never left of a condition that can still fail: a burnt key is silent, and
// permanent for the page.
export function markSearchReported(tabKey: string, filters: LocationFilters): boolean {
  // Keyed by the carrier ID, not its label: the id is what the read was filtered by, so a backend
  // rename would otherwise count one search twice. An absent carrier keys as `all` — the same
  // vocabulary the control uses — rather than leaning on `String(undefined)` spelling a token no
  // real id can spell. `box ?? ''` so an absent optional filter is not a distinct search from an
  // empty one.
  //
  // The states join the key too, and that is not a formality: this key enumerates the filters BY
  // HAND, so a field left out of it is invisible here — and since the key is CONSUMED and the set
  // lives for the life of the page, every later edit of that field would be silent for good rather
  // than once. `'all'` for the absent filter, the same token the carrier uses. The array arrives
  // canonically ordered, so one selection has one key however it was clicked.
  const key = `${tabKey}|${String(filters.carrier ?? 'all')}|${String(filters.days)}|${filters.box ?? ''}|${filters.states?.join(',') ?? 'all'}`;
  if (reported.has(key)) return false;
  reported.add(key);
  return true;
}

// Test seam: module state outlives a `render()`, so cases in one file would otherwise leak into each
// other. Mirrors `resetBoxStatusMock` / `resetLockCodesMock` in the mocks package.
export function resetReportedSearches(): void {
  reported.clear();
}
